import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "./Reveal";

export function PageHero({ eyebrow, titleLines, intro, badges = [], image, imageAlt }) {
  return (
    <section className="relative overflow-hidden pt-40 pb-20 lg:pt-48 lg:pb-28">
      <div className="absolute inset-0 bg-grid-fade opacity-60" aria-hidden="true" />
      {image && (
        <div className="absolute inset-0" aria-hidden="true">
          <img src={image} alt="" className="w-full h-full object-cover opacity-[0.14]" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink-900/70 via-ink-900/85 to-ink-900" />
        </div>
      )}

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className={`grid gap-14 items-center ${image ? "lg:grid-cols-[1.15fr_1fr]" : ""}`}>
          <div>
            {eyebrow && (
              <Reveal delay={0.05}>
                <span className="eyebrow mb-7">{eyebrow}</span>
              </Reveal>
            )}
            <MaskedLines
              as="h1"
              lines={titleLines}
              delay={0.15}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.04]"
            />
            {intro && (
              <Reveal delay={0.5}>
                <p className="mt-7 text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl">
                  {intro}
                </p>
              </Reveal>
            )}
            {badges.length > 0 && (
              <Reveal delay={0.65}>
                <div className="mt-8 flex flex-wrap gap-2.5">
                  {badges.map((b) => (
                    <span
                      key={b}
                      data-testid="page-hero-badge"
                      className="px-4 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.18em] text-gold-200 border border-gold-400/30 bg-gold-400/[0.07]"
                    >
                      {b}
                    </span>
                  ))}
                </div>
              </Reveal>
            )}
          </div>

          {image && (
            <Reveal delay={0.35} y={36} className="hidden lg:block">
              <div className="frame-gold relative rounded-sm overflow-hidden" data-testid="page-hero-image">
                <img
                  src={image}
                  alt={imageAlt || ""}
                  className="w-full h-[380px] object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-transparent to-ink-900/30" />
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

export function CTABand({ title, subtitle, primary, secondary, testid = "cta-band" }) {
  return (
    <section className="relative py-24 lg:py-32 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.12]"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/30704246/pexels-photo-30704246.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink-900 via-ink-900/60 to-ink-900" aria-hidden="true" />
      <Reveal className="relative mx-auto max-w-3xl px-5 sm:px-8 text-center flex flex-col items-center gap-7">
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
          {title}
        </h2>
        {subtitle && <p className="text-base md:text-lg text-zinc-400 leading-relaxed">{subtitle}</p>}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-2">
          {primary?.external ? (
            <a href={primary.to} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-primary`} className="btn-gold">
              {primary.label}
              <ArrowRight size={16} />
            </a>
          ) : (
            <Link to={primary?.to || "/contact"} data-testid={`${testid}-primary`} className="btn-gold">
              {primary?.label || "Contact Our Team"}
              <ArrowRight size={16} />
            </Link>
          )}
          {secondary && (
            <Link to={secondary.to} data-testid={`${testid}-secondary`} className="btn-outline">
              {secondary.label}
            </Link>
          )}
        </div>
      </Reveal>
    </section>
  );
}